import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { FaCalendarDays, FaLocationDot } from "react-icons/fa6";
import { MdDateRange, MdWorkOutline } from "react-icons/md";
import { themeBlue, themeOrange } from "../../utils/colorTheme";
import { useDispatch, useSelector } from "react-redux";
import { acceptJobAsync, getJobsAsync } from "../../services/services";
import { asyncStatus } from "../../utils/asyncStatus";
import {
  error_toast_message,
  success_toast_message,
} from "../../utils/toast_message";
import { sendMessageNotification } from "../../services/chatService";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";

import { format, addDays } from "date-fns";
import { setJobAcceptStatus } from "../../store/slices/services_slice";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { getChatUsersListAsync } from "../../services/chatService";
import { BsInboxFill } from "react-icons/bs";

const customMarkerIcon = new L.Icon({
  iconUrl: markerIconPng,
  shadowUrl: markerShadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const AVAILABLE_SHIFTS = ["Morning", "Afternoon", "Evening"];

// Unified Calendar Modal Component
const UnifiedCalendarModal = ({
  open,
  onClose,
  onConfirm,
  selectedDate,
  selectedShift,
  availableDates = [],
  jobAcceptLoading = false,
}) => {
  const [tempSelectedDate, setTempSelectedDate] = useState("");
  const [tempSelectedShift, setTempSelectedShift] = useState("");
  const [visibleDatesCount, setVisibleDatesCount] = useState(4);

  useEffect(() => {
    if (open) {
      setTempSelectedDate(selectedDate || "");
      setTempSelectedShift(selectedShift || "");
      setVisibleDatesCount(4);
    }
  }, [open, selectedDate, selectedShift]);

  const generateDateOptions = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = addDays(today, i);
      const dateString = format(date, "yyyy-MM-dd");

      const availableDate = availableDates.find((avail) => {
        try {
          if (!avail?.selected_date || typeof avail.selected_date !== "string") return false;
          const [year, month, day] = avail.selected_date.split("-");
          const localDate = new Date(year, month - 1, day);
          if (isNaN(localDate.getTime())) return false;
          return format(localDate, "yyyy-MM-dd") === dateString;
        } catch {
          return false;
        }
      });

      dates.push({
        value: dateString,
        label: format(date, "EEE, MMM d, yyyy"),
        date: date,
        isAvailable: !!availableDate,
        availableShifts: availableDate?.shifts || [],
        type: availableDate ? "available" : "custom",
      });
    }
    return dates;
  };

  const dateOptions = generateDateOptions();
  const datesToShow = dateOptions.slice(0, visibleDatesCount);
  const hasMoreDates = dateOptions.length > visibleDatesCount;
  const canShowLess = visibleDatesCount > 4;

  const handleShowMore = () => setVisibleDatesCount((prev) => Math.min(prev + 3, dateOptions.length));
  const handleShowLess = () => setVisibleDatesCount((prev) => Math.max(prev - 3, 4));

  const handleDateSelect = (dateValue) => {
    setTempSelectedDate(dateValue);
    setTempSelectedShift("");
  };

  const handleShiftSelect = (shift) => setTempSelectedShift(shift);

  const handleConfirm = () => {
    if (tempSelectedDate && tempSelectedShift) {
      const selectedDateObj = dateOptions.find((opt) => opt.value === tempSelectedDate);
      onConfirm({
        date: tempSelectedDate,
        shift: tempSelectedShift,
        label: selectedDateObj?.label || tempSelectedDate,
        type: selectedDateObj?.type || "custom",
      });
    }
  };

  const handleClear = () => {
    setTempSelectedDate("");
    setTempSelectedShift("");
    onConfirm(null);
    onClose();
  };

  const getShiftsForSelectedDate = () => {
    const selectedDateObj = dateOptions.find((opt) => opt.value === tempSelectedDate);
    if (!selectedDateObj) return [];
    return selectedDateObj.isAvailable ? selectedDateObj.availableShifts : AVAILABLE_SHIFTS;
  };

  const shiftsToShow = getShiftsForSelectedDate();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      {/* Modal header stripe */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          background: "linear-gradient(90deg, #F15A24 0%, #C53F10 100%)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <MdDateRange size={16} color="white" />
        <Typography sx={{ color: "white", fontSize: "13px", fontWeight: "700", letterSpacing: "0.5px" }}>
          SELECT DATE & SHIFT
        </Typography>
      </Box>

      <DialogTitle sx={{ pb: 0, pt: 2.5 }}>
        <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#1a1a1a" }}>
          Choose Your Available Date & Shift
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack gap={3} sx={{ mt: 2 }}>
          {/* Date Selection */}
          <Stack gap={2}>
            <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Choose a Date
            </Typography>

            <Grid container spacing={1}>
              {datesToShow.map((option) => {
                const isSelected = tempSelectedDate === option.value;
                const isToday = format(new Date(), "yyyy-MM-dd") === option.value;
                const isAvailable = option.isAvailable;

                return (
                  <Grid item xs={6} sm={4} md={3} key={option.value}>
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => handleDateSelect(option.value)}
                      sx={{
                        backgroundColor: isSelected
                          ? themeOrange
                          : isAvailable
                          ? "#e8f5e9"
                          : "transparent",
                        border: `1.5px solid ${
                          isSelected ? themeOrange : isAvailable ? "#4CAF50" : isToday ? themeOrange : "#e0e0e0"
                        }`,
                        borderRadius: "10px",
                        color: isSelected ? "white" : isAvailable ? "#2E7D32" : isToday ? themeOrange : "#555",
                        "&:hover": {
                          backgroundColor: isSelected ? "#d14e20" : isAvailable ? "#C8E6C9" : "#f5f5f5",
                        },
                        fontSize: "0.75rem",
                        py: 1,
                        position: "relative",
                        textTransform: "none",
                      }}
                    >
                      <Stack alignItems="center">
                        {format(option.date, "MMM d")}
                        {isToday && (
                          <Typography variant="caption" component="div" sx={{ fontSize: "0.6rem" }}>
                            Today
                          </Typography>
                        )}
                        {isAvailable && (
                          <Chip
                            label="Preferred"
                            size="small"
                            sx={{
                              height: "12px",
                              fontSize: "0.5rem",
                              backgroundColor: "#4CAF50",
                              color: "white",
                              position: "absolute",
                              top: 2,
                              right: 2,
                            }}
                          />
                        )}
                      </Stack>
                    </Button>
                  </Grid>
                );
              })}

              {(hasMoreDates || canShowLess) && (
                <Grid item xs={12}>
                  <Stack direction="row" gap={1} justifyContent="center" mt={1}>
                    {hasMoreDates && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleShowMore}
                        sx={{
                          borderColor: themeOrange,
                          color: themeOrange,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          px: 3,
                          "&:hover": { backgroundColor: "rgba(241,90,36,0.04)", borderColor: themeOrange },
                        }}
                      >
                        Show More (+3)
                      </Button>
                    )}
                    {canShowLess && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleShowLess}
                        sx={{
                          color: themeOrange,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          px: 3,
                          "&:hover": { backgroundColor: "rgba(241,90,36,0.04)" },
                        }}
                      >
                        Show Less (-3)
                      </Button>
                    )}
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Stack>

          {/* Shift Selection */}
          {tempSelectedDate && (
            <>
              <Divider />
              <Stack gap={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Choose a Shift
                  </Typography>
                  {dateOptions.find((opt) => opt.value === tempSelectedDate)?.isAvailable && (
                    <Chip
                      label="Customer's preferred shifts"
                      size="small"
                      sx={{ backgroundColor: "#4CAF50", color: "white", fontSize: "0.6rem" }}
                    />
                  )}
                </Stack>

                <Stack direction="row" gap={2} flexWrap="wrap">
                  {shiftsToShow.map((shift) => (
                    <Button
                      key={shift}
                      onClick={() => handleShiftSelect(shift)}
                      sx={{
                        background: tempSelectedShift === shift
                          ? "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)"
                          : "transparent",
                        border: `1.5px solid ${themeOrange}`,
                        borderRadius: "10px",
                        color: tempSelectedShift === shift ? "white" : themeOrange,
                        textTransform: "none",
                        fontWeight: "600",
                        px: 3,
                        py: 1,
                        minWidth: "120px",
                        "&:hover": {
                          background: tempSelectedShift === shift
                            ? "linear-gradient(180deg, #d14e20 0%, #b3370d 100%)"
                            : "rgba(241,90,36,0.06)",
                        },
                      }}
                    >
                      {shift}
                    </Button>
                  ))}
                </Stack>

                {shiftsToShow.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No shifts available for this date
                  </Typography>
                )}
              </Stack>
            </>
          )}

          {/* Current Selection Summary */}
          {tempSelectedDate && tempSelectedShift && (
            <>
              <Divider />
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: `1.5px solid ${themeOrange}40`,
                  backgroundColor: `${themeOrange}08`,
                }}
              >
                <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", mb: 1 }}>
                  Selected
                </Typography>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={`${dateOptions.find((opt) => opt.value === tempSelectedDate)?.label} — ${tempSelectedShift}`}
                    sx={{ backgroundColor: themeOrange, color: "white", fontWeight: "600" }}
                  />
                  {dateOptions.find((opt) => opt.value === tempSelectedDate)?.type === "available" && (
                    <Chip
                      label="Customer Preferred"
                      size="small"
                      sx={{ backgroundColor: "#4CAF50", color: "white", fontSize: "0.65rem" }}
                    />
                  )}
                  {dateOptions.find((opt) => opt.value === tempSelectedDate)?.type === "custom" && (
                    <Chip
                      label="Custom Date"
                      size="small"
                      sx={{ backgroundColor: themeOrange, color: "white", fontSize: "0.65rem" }}
                    />
                  )}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#555",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: "600",
            border: "1.5px solid #e0e0e0",
            px: 3,
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          Cancel
        </Button>
        {selectedDate && (
          <Button
            onClick={handleClear}
            variant="outlined"
            sx={{
              borderColor: "#ef9a9a",
              color: "#c62828",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: "600",
              px: 3,
              "&:hover": { backgroundColor: "#ffeaea", borderColor: "#ef9a9a" },
            }}
          >
            Clear
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          disabled={!tempSelectedDate || !tempSelectedShift}
          sx={{
            background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
            color: "white",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: "700",
            px: 3,
            py: 1,
            boxShadow: "0 4px 12px rgba(241,90,36,0.25)",
            "&:hover": { background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)" },
            "&.Mui-disabled": { opacity: 0.5, color: "white" },
          }}
        >
          {jobAcceptLoading ? (
            <CircularProgress size={18} sx={{ color: "white" }} />
          ) : (
            "Confirm"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const socket = io("https://chat-soft-2f178ffd0d28.herokuapp.com");

const JobRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [positions, setPositions] = useState({});
  const [selectedUserData, setSelectedUserData] = useState({});
  const [datePickerOpen, setDatePickerOpen] = useState({});
  const [selectedDateShift, setSelectedDateShift] = useState({});

  const { get_jobs_status, get_jobs_data, accept_job_status } = useSelector(
    (state) => state.serviceManage
  );
  const { user } = useSelector((state) => state.userAuth);

  const getJobsLoader = get_jobs_status === asyncStatus.LOADING;
  const jobAcceptLoading = accept_job_status === asyncStatus.LOADING;

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  useEffect(() => {
    const fetchCoordinates = async (location, jobId) => {
      try {
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${location}&key=${apiKey}`
        );
        const result = response?.data?.results[0];
        const { lat, lng } = result?.geometry?.location || {};
        if (lat && lng) {
          setPositions((prev) => ({ ...prev, [jobId]: [lat, lng] }));
        }
      } catch (error) {
        // geocoding error
      }
    };

    if (get_jobs_data?.length) {
      get_jobs_data.forEach((job) => {
        if (job?.location) fetchCoordinates(job.location, job.id);
      });
    }
  }, [get_jobs_data, apiKey]);

  const userStatus = user?.status === 1;

  const _handleMapClick = (position) => {
    if (userStatus && position) {
      window.open(`https://www.google.com/maps?q=${position[0]},${position[1]}`, "_blank");
    }
  };

  const _getMainLocation = (location) => (location ? location.split(",")[0] : "");

  useEffect(() => {
    dispatch(getJobsAsync());
    const interval = setInterval(() => {
      dispatch(getJobsAsync());
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenDatePicker = (jobId) => setDatePickerOpen((prev) => ({ ...prev, [jobId]: true }));
  const handleCloseDatePicker = (jobId) => setDatePickerOpen((prev) => ({ ...prev, [jobId]: false }));

  const handleConfirmDateShift = (jobId, selection) => {
    const currentJob = get_jobs_data?.find((job) => job.id === jobId);
    const customerId = currentJob?.user_id;
    const customerData = {
      id: customerId,
      first_name: currentJob?.user?.first_name,
      last_name: currentJob?.user?.last_name,
      avatar: currentJob?.user?.avatar,
    };

    setSelectedDateShift((prev) => ({ ...prev, [jobId]: selection }));
    setSelectedJobId(jobId);
    setSelectedUserData(customerData);

    dispatch(
      acceptJobAsync({
        order_id: jobId,
        date_time_lock: { date: selection?.date, shift: selection?.shift },
      })
    );
  };

  const _handleAccept = (values, customerId) => {
    const currentJob = get_jobs_data?.find((job) => job.id === values.id);
    const customerData = {
      id: customerId,
      first_name: currentJob?.user?.first_name,
      last_name: currentJob?.user?.last_name,
      avatar: currentJob?.user?.avatar,
    };

    setSelectedJobId(values.id);
    setSelectedUserData(customerData);

    dispatch(
      acceptJobAsync({
        order_id: values?.id,
        date_time_lock: { date: values?.date, shift: values?.shift },
      })
    );
  };

  useEffect(() => {
    if (accept_job_status === asyncStatus.ERROR) {
      error_toast_message("Job already accepted by another pro");
    } else if (accept_job_status === asyncStatus.SUCCEEDED) {
      success_toast_message("Job accepted successfully");
    }

    if (accept_job_status === asyncStatus.SUCCEEDED && selectedUserData?.id && user?.id) {
      const createRoomId = (userId1, userId2) => {
        const ids = [Number(userId1), Number(userId2)].sort((a, b) => a - b);
        return `${ids[0]}-${ids[1]}`;
      };

      const roomId = createRoomId(user.id, selectedUserData.id);
      const automaticMessage = {
        message: `Hi! I'm ${user?.first_name}, your pro. Let me know if there's anything else you'd like to discuss before our appointment!`,
        roomId,
        senderId: user.id,
        receiverId: selectedUserData.id,
        createdAt: new Date().toISOString(),
        companyId: "66c7dcd95dbdcd5ccb67432a",
      };

      if (!socket.connected) socket.connect();
      socket.emit("join-room", roomId);

      setTimeout(() => {
        socket.emit("message", automaticMessage);
        sendMessageNotification(selectedUserData.id, automaticMessage.message);
        dispatch(getChatUsersListAsync());

        setTimeout(() => {
          navigate(`/dashboard/message`, {
            state: {
              id: selectedUserData?.id,
              first_name: selectedUserData?.first_name,
              last_name: selectedUserData?.last_name,
              avatar: selectedUserData?.avatar,
              isNewConversation: true,
            },
          });
          dispatch(setJobAcceptStatus());
        }, 1500);
      }, 500);
    }
  }, [accept_job_status, user, selectedUserData, navigate, dispatch]);

  const sortedJobs = [...(get_jobs_data || [])]
    .filter((job) => job.status !== "Cancelled")
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <Stack bgcolor="#FAFAFA" minHeight="100vh">
      <Container sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" }, py: 4 }}>
        <Stack gap={3}>
          {/* Page Heading */}
          <Stack>
            <Typography
              sx={{
                fontSize: "30px",
                fontWeight: "500",
                color: "#333",
                fontFamily: "Raleway",
              }}
            >
              Job{" "}
              <Typography
                component="span"
                sx={{
                  fontSize: "30px",
                  fontWeight: "700",
                  color: "#333",
                  fontFamily: "Raleway",
                }}
              >
                Requests
              </Typography>
            </Typography>
            <hr color="#D1D1D1" style={{ margin: "8px 0 0 0" }} />
          </Stack>

          {getJobsLoader ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
              <CircularProgress size={30} sx={{ color: themeOrange }} />
            </Stack>
          ) : sortedJobs?.length > 0 ? (
            sortedJobs.map(({
              date,
              description,
              id,
              location,
              status,
              time,
              user_id,
              user: customer,
              service,
              images,
            }) => {
              const position = positions[id];
              const displayLocation = userStatus ? location : _getMainLocation(location);
              const availableDates = Array.isArray(date) ? date : [];
              const selection = selectedDateShift[id];

              return (
                <Stack
                  key={id}
                  sx={{
                    borderRadius: "16px",
                    border: "1.5px solid #ebebeb",
                    backgroundColor: "white",
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Card header stripe */}
                  <Stack
                    sx={{
                      px: 2.5,
                      py: 1.25,
                      background: "linear-gradient(90deg, #F15A24 0%, #C53F10 100%)",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <MdWorkOutline size={16} color="white" />
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: "700",
                          fontSize: "15px",
                          letterSpacing: "0.2px",
                        }}
                      >
                        {service?.name || "Service Request"}
                      </Typography>
                    </Stack>
                    <Chip
                      label="New Request"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: "700",
                        fontSize: "11px",
                        border: "1px solid rgba(255,255,255,0.35)",
                      }}
                    />
                  </Stack>

                  <Stack p={3} gap={3}>
                    <Grid container spacing={3}>
                      {/* Left — Customer & Job Info */}
                      <Grid item xs={12} md={7}>
                        <Stack gap={2.5}>
                          {/* Customer info card */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={2}
                            p={2}
                            sx={{
                              backgroundColor: "#fafafa",
                              borderRadius: "12px",
                              border: "1.5px solid #ebebeb",
                            }}
                          >
                            <img
                              style={{
                                width: "68px",
                                height: "68px",
                                objectFit: "cover",
                                borderRadius: "50%",
                                border: "3px solid white",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                              src={customer?.avatar || ""}
                              alt=""
                            />
                            <Stack gap={0.5}>
                              <Typography sx={{ fontWeight: "700", fontSize: "16px", color: "#1a1a1a" }}>
                                {`${customer?.first_name || ""} ${customer?.last_name || ""}`}
                              </Typography>
                              {customer?.email && (
                                <Typography sx={{ color: "#888", fontSize: "13px" }}>
                                  {customer.email}
                                </Typography>
                              )}
                              {customer?.phone && (
                                <Typography sx={{ color: "#888", fontSize: "13px" }}>
                                  {customer.phone}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>

                          {/* Location */}
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            gap={1.5}
                            p={1.5}
                            sx={{
                              backgroundColor: "#fafafa",
                              borderRadius: "10px",
                              border: "1.5px solid #ebebeb",
                            }}
                          >
                            <FaLocationDot
                              size={18}
                              style={{ color: themeOrange, flexShrink: 0, marginTop: "2px" }}
                            />
                            <Typography sx={{ fontSize: "14px", color: "#444" }}>
                              {displayLocation || "Location not specified"}
                            </Typography>
                          </Stack>

                          {/* Job Description */}
                          {description && (
                            <Stack gap={1}>
                              <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Job Description
                              </Typography>
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: "10px",
                                  border: "1.5px solid #ebebeb",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                <Typography sx={{ fontSize: "14px", color: "#555", lineHeight: 1.7 }}>
                                  {description}
                                </Typography>
                              </Box>
                            </Stack>
                          )}

                          {/* Timing Constraints */}
                          {time && (
                            <Stack gap={1}>
                              <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Timing Constraints
                              </Typography>
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: "10px",
                                  border: `1.5px solid ${themeOrange}40`,
                                  backgroundColor: `${themeOrange}08`,
                                }}
                              >
                                <Typography sx={{ fontSize: "14px", color: "#555" }}>
                                  {time}
                                </Typography>
                              </Box>
                            </Stack>
                          )}

                          {/* Requested Dates */}
                          <Stack gap={1}>
                            <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Requested Dates
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {Array.isArray(date) && date.map(({ selected_date, shifts }) => {
                                if (!selected_date || typeof selected_date !== "string") return null;
                                return (
                                  <Chip
                                    key={selected_date}
                                    icon={<FaCalendarDays size={11} />}
                                    label={(() => {
                                      try {
                                        const [year, month, day] = selected_date.split("-");
                                        const localDate = new Date(year, month - 1, day);
                                        if (isNaN(localDate.getTime())) return selected_date;
                                        return `${format(localDate, "MMM d")} (${shifts?.map((s) => s?.slice(0, 3)).join(", ")})`;
                                      } catch {
                                        return selected_date;
                                      }
                                    })()}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#e3f2fd",
                                      color: themeBlue,
                                      fontWeight: "600",
                                      border: `1px solid ${themeBlue}30`,
                                    }}
                                  />
                                );
                              })}
                            </Stack>
                          </Stack>

                          {/* Attached Images */}
                          {images?.length > 0 && (
                            <Stack gap={1}>
                              <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Attached Images
                              </Typography>
                              <Stack direction="row" gap={1} flexWrap="wrap">
                                {images.map(({ url, caption }) => (
                                  <img
                                    key={url}
                                    src={url}
                                    alt={caption || ""}
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "1.5px solid #ebebeb",
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Stack>
                          )}
                        </Stack>
                      </Grid>

                      {/* Right — Map */}
                      <Grid item xs={12} md={5}>
                        <Stack gap={2} sx={{ height: "100%" }}>
                          <div
                            style={{
                              height: "260px",
                              width: "100%",
                              borderRadius: "12px",
                              overflow: "hidden",
                              border: "1.5px solid #ebebeb",
                            }}
                          >
                            {position ? (
                              <MapContainer
                                center={position}
                                zoom={13}
                                style={{ height: "100%", width: "100%" }}
                              >
                                <TileLayer
                                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <Marker
                                  icon={customMarkerIcon}
                                  eventHandlers={
                                    userStatus ? { click: () => _handleMapClick(position) } : {}
                                  }
                                  position={position}
                                >
                                  {userStatus && <Popup>{location}</Popup>}
                                </Marker>
                              </MapContainer>
                            ) : (
                              <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{ height: "100%", backgroundColor: "#f5f5f5" }}
                              >
                                <CircularProgress size={20} sx={{ color: themeOrange }} />
                              </Stack>
                            )}
                          </div>

                          {/* Selected Date Display */}
                          {selection && (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: "12px",
                                border: `1.5px solid ${selection.type === "available" ? "#4CAF5060" : `${themeOrange}60`}`,
                                backgroundColor: selection.type === "available" ? "#f1f8f1" : `${themeOrange}08`,
                              }}
                            >
                              <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", mb: 1 }}>
                                Selected Date
                              </Typography>
                              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <FaCalendarDays
                                  size={15}
                                  style={{ color: selection.type === "available" ? "#4CAF50" : themeOrange }}
                                />
                                <Typography sx={{ fontWeight: "600", fontSize: "13px", color: "#1a1a1a" }}>
                                  {selection.label} — {selection.shift}
                                </Typography>
                                <Chip
                                  label={selection.type === "available" ? "Preferred" : "Custom"}
                                  size="small"
                                  sx={{
                                    backgroundColor: selection.type === "available" ? "#4CAF50" : themeOrange,
                                    color: "white",
                                    fontSize: "10px",
                                    height: "20px",
                                    fontWeight: "600",
                                  }}
                                />
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>

                    <Divider />

                    {/* Action Row */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                      <Button
                        variant="outlined"
                        startIcon={<FaCalendarDays size={14} />}
                        onClick={() => handleOpenDatePicker(id)}
                        sx={{
                          borderColor: themeOrange,
                          color: themeOrange,
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: "600",
                          px: 3,
                          py: 1,
                          "&:hover": {
                            borderColor: themeOrange,
                            backgroundColor: "rgba(241,90,36,0.06)",
                          },
                        }}
                      >
                        {selection ? "Change Date & Shift" : "Select Date & Shift"}
                      </Button>

                      {userStatus && selection && (
                        <Button
                          variant="contained"
                          disabled={jobAcceptLoading && selectedJobId === id}
                          onClick={() =>
                            _handleAccept(
                              { id, date: selection.date, shift: selection.shift },
                              user_id
                            )
                          }
                          sx={{
                            background: "linear-gradient(180deg, #43A047 0%, #2E7D32 100%)",
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: "700",
                            fontSize: "14px",
                            px: 4,
                            py: 1.25,
                            boxShadow: "0 4px 12px rgba(67,160,71,0.25)",
                            "&:hover": {
                              background: "linear-gradient(180deg, #388E3C 0%, #27632a 100%)",
                              boxShadow: "0 4px 14px rgba(67,160,71,0.35)",
                            },
                            "&.Mui-disabled": { opacity: 0.6 },
                          }}
                        >
                          {jobAcceptLoading && selectedJobId === id ? (
                            <CircularProgress size={18} sx={{ color: "white" }} />
                          ) : (
                            "Accept Job"
                          )}
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <UnifiedCalendarModal
                    open={datePickerOpen[id] || false}
                    onClose={() => handleCloseDatePicker(id)}
                    onConfirm={(sel) => handleConfirmDateShift(id, sel)}
                    selectedDate={selection?.date || ""}
                    selectedShift={selection?.shift || ""}
                    availableDates={availableDates}
                    jobAcceptLoading={jobAcceptLoading}
                  />
                </Stack>
              );
            })
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "40vh" }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <BsInboxFill size={36} color="#ccc" />
              </Box>
              <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#555" }}>
                No Job Requests
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#aaa", mt: 0.5 }}>
                New requests will appear here when customers book your services.
              </Typography>
            </Stack>
          )}
        </Stack>
      </Container>
    </Stack>
  );
};

export default JobRequest;
